/**
 * Customer notes (client-facing invoice footer text).
 * Stored in invoices.notes; org default in settings.invoices.default_customer_notes.
 */

import { supabase } from "../supabase";
import { getEffectiveOrgSetting } from "./organizationSettings";
import { CUSTOMER_NOTES_TITLE } from "./invoiceCommercialSections";
import { loadActorOrganizationSettings } from "./invoiceOrgSettings";

export function normalizeCustomerNotes(value) {
  const text = String(value || "").trim();
  return text || "";
}

export function hasCustomerNotes(value) {
  return Boolean(normalizeCustomerNotes(value));
}

export function serializeCustomerNotes(value) {
  const text = normalizeCustomerNotes(value);
  return text || null;
}

export function getOrganizationCustomerNotes(settings) {
  return normalizeCustomerNotes(
    getEffectiveOrgSetting(settings, "invoices.default_customer_notes", ""),
  );
}

export async function loadDefaultCustomerNotes(actor, client = supabase) {
  try {
    const settings = await loadActorOrganizationSettings(actor, client);
    return getOrganizationCustomerNotes(settings);
  } catch (err) {
    console.warn("[invoiceCustomerNotes] default fallback", err);
    return "";
  }
}

export function resolveInvoiceCustomerNotes({
  invoice = null,
  organizationCustomerNotes = null,
} = {}) {
  const override = invoice?.customer_notes_override === true;
  if (override) {
    return normalizeCustomerNotes(invoice?.notes);
  }

  if (hasCustomerNotes(organizationCustomerNotes)) {
    return normalizeCustomerNotes(organizationCustomerNotes);
  }

  if (hasCustomerNotes(invoice?.notes)) {
    return normalizeCustomerNotes(invoice.notes);
  }

  return "";
}

export function buildCustomerNotesPresentation(raw, options = {}) {
  const notes = normalizeCustomerNotes(raw);
  if (!notes) return null;

  return {
    title: options.title || CUSTOMER_NOTES_TITLE,
    notes,
    blocks: [{ type: "text", text: notes }],
  };
}
